import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import {
  Header,
  HeaderBackButton,
  HeaderButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import { MainScrollArea, MainHeader, Main, MainHeaderBackButton } from '@/app/ui/components/main';
import { H1 } from '@/app/ui/components/heading';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { HugeiconsIcon } from '@hugeicons/react';
import { UserGroupIcon, PlusSignIcon, Building06Icon } from '@hugeicons/core-free-icons';
import NewProjectMembersList from '@/app/components/features/projects/projectAccess/NewProjectMembersList';
import { Button } from '@/app/ui/components/button';
import AddProjectMembersDialog from '@/app/components/features/projects/projectAccess/AddProjectMembersDialog';
import { useState } from 'react';
import { getIsProjectOwner } from '@/app/utils/helpers';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute('/_authed/project/$projectId/_layout/(home)/members')({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations();
  const { projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const isOwner = getIsProjectOwner(project?.roleName);

  return (
    <Main>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to={'/'}>
                <HugeiconsIcon icon={Building06Icon} />
                {t('pages.homepage.breadcrumbs.projects')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink to={'/project/$projectId'} params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={UserGroupIcon} />
                {t('pages.projectMembers.title')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        {isOwner && (
          <HeaderButton
            aria-label={t('pages.projectMembers.addDialog.addButton')}
            onClick={() => setAddDialogOpen(true)}
          >
            <HugeiconsIcon icon={PlusSignIcon} />
          </HeaderButton>
        )}
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>
            <MainHeaderBackButton />
            {t('pages.projectMembers.title')}
          </H1>
          {isOwner && (
            <Button kind="with-icon" onClick={() => setAddDialogOpen(true)}>
              <HugeiconsIcon icon={PlusSignIcon} />
              {t('pages.projectMembers.addDialog.addButton')}
            </Button>
          )}
        </MainHeader>
        <NewProjectMembersList />
        <AddProjectMembersDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          projectId={projectId}
        />
      </MainScrollArea>
    </Main>
  );
}
